import type { ReactNode } from 'react';
import { Box, Drawer, Stack } from '@rossum/ui/material';
import { useResizableDrawer } from '../hooks/useResizableDrawer';

type ResizeHandleProps = {
  isResizing: boolean;
  onResizeStart: (e: React.MouseEvent) => void;
};

const ResizeHandle = ({ isResizing, onResizeStart }: ResizeHandleProps) => (
  <Box
    onMouseDown={onResizeStart}
    sx={{
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      cursor: 'col-resize',
      zIndex: 1,
      '&:hover': {
        bgcolor: 'action.hover',
      },
      ...(isResizing && {
        bgcolor: 'action.selected',
      }),
    }}
  />
);

/** Separate so `useResizableDrawer` is not called at all by a fixed-width panel. */
const ResizableDrawer = ({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) => {
  const { width, isResizing, handleResizeStart } = useResizableDrawer();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      elevation={2}
      // `overflow: visible` so the resize handle is not clipped by the paper.
      PaperProps={{ sx: { width, maxWidth: '100vw', overflow: 'visible' } }}
    >
      <ResizeHandle isResizing={isResizing} onResizeStart={handleResizeStart} />
      {children}
    </Drawer>
  );
};

/**
 * The right-hand agent drawer: its chrome, without any opinion about what a message is.
 *
 * Shared so a second agent panel looks like the chat one without borrowing its state — the chat
 * store, its `chatId` and its history are all on the other side of this seam. Callers pass their own
 * header, footer and body, and keep their own conversation model.
 *
 * Children mount only while open, which is what resets a panel's transient UI between visits.
 *
 * `header`/`footer` are optional: a caller that already lays out its own column — the main chat
 * panel does, with a task list and an error slot between the two — passes only `children`.
 */
export const ChatDrawerShell = ({
  open,
  onClose,
  header,
  footer,
  children,
  width,
}: {
  open: boolean;
  onClose: () => void;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  /** Fixed width. Omit to let the user resize, as the main chat panel does. */
  width?: number;
}) => {
  const hasOwnLayout = header === undefined && footer === undefined;

  const body = !open ? null : hasOwnLayout ? (
    children
  ) : (
    <Stack sx={{ height: '100%', position: 'relative' }}>
      {header}
      {children}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>{footer}</Box>
    </Stack>
  );

  return width === undefined ? (
    <ResizableDrawer open={open} onClose={onClose}>
      {body}
    </ResizableDrawer>
  ) : (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      elevation={2}
      PaperProps={{ sx: { width, maxWidth: '100vw' } }}
    >
      {body}
    </Drawer>
  );
};
